import React from 'react'
import ReactMarkdown from 'react-markdown'
import Box from '../kit/box'
import Text, { TextProps } from '../kit/text'

/* -----------------------------------------------------------------------------
 * Markdown (ReactMarkdown wrapper)
 * - Default text color: walrusBlue200
 * - Default font family: GT Walsheim (using display.* tokens for headings)
 * -------------------------------------------------------------------------- */

export type MarkdownProps = {
  children: string
  textColor?: TextProps['textColor']
  componentsOverrides?: Parameters<typeof ReactMarkdown>[0]['components']
  className?: string
}

// Map heading levels to theme font tokens (all GT Walsheim bold)
const headingFont = {
  1: 'display.24.bold',
  2: 'display.20.bold',
  3: 'display.18.bold',
  4: 'display.16.bold',
  5: 'display.14.bold',
  6: 'display.12.bold'
} as { [key: number]: TextProps['font'] }

const defaultFont = {
  fontFamily: `'Inter', sans-serif`,
  fontSize: 15,
  lineHeight: 20
}

export const Markdown = ({
  children,
  textColor = 'walrusBlue200',
  componentsOverrides,
  className
}: MarkdownProps) => {
  const components = {
    h1: ({ children: ch }: any) => (
      <Text font={headingFont[1]} textColor={textColor} mb='12'>
        {ch}
      </Text>
    ),
    h2: ({ children: ch }: any) => (
      <Text font={headingFont[2]} textColor={textColor} mb='12'>
        {ch}
      </Text>
    ),
    h3: ({ children: ch }: any) => (
      <Text font={headingFont[3]} textColor={textColor} mb='08'>
        {ch}
      </Text>
    ),
    h4: ({ children: ch }: any) => (
      <Text font={headingFont[4]} textColor={textColor} mb='08'>
        {ch}
      </Text>
    ),
    h5: ({ children: ch }: any) => (
      <Text font={headingFont[5]} textColor={textColor} mb='08'>
        {ch}
      </Text>
    ),
    h6: ({ children: ch }: any) => (
      <Text font={headingFont[6]} textColor={textColor} mb='08'>
        {ch}
      </Text>
    ),
    p: ({ children: ch }: any) => (
      <Text
        textColor={textColor}
        fontFamily={defaultFont.fontFamily}
        fontSize={defaultFont.fontSize}
        lineHeight={defaultFont.lineHeight}
        mb='08'
      >
        {ch}
      </Text>
    ),
    strong: ({ children: ch }: any) => (
      <Text font='display.14.bold' textColor={textColor}>
        {ch}
      </Text>
    ),
    em: ({ children: ch }: any) => (
      <Text
        textColor={textColor}
        fontFamily={defaultFont.fontFamily}
        fontSize={defaultFont.fontSize}
        lineHeight={defaultFont.lineHeight}
        fontStyle='italic'
      >
        {ch}
      </Text>
    ),
    code: ({ inline, children: ch }: any) => {
      if (inline) {
        return (
          <Text
            fontFamily='monospace'
            textColor={textColor}
            backgroundColor='paleViolet'
            paddingX='04'
            paddingY='02'
            style={{ borderRadius: 4 }}
          >
            {ch}
          </Text>
        )
      }
      return (
        <Box
          style={{
            backgroundColor: '#F1F2FE',
            padding: 12,
            borderRadius: 4,
            marginBottom: 16
          }}
        >
          <Text fontFamily='monospace' textColor={textColor}>
            {ch}
          </Text>
        </Box>
      )
    },
    blockquote: ({ children: ch }: any) => (
      <Box
        style={{
          borderLeftWidth: 4,
          borderLeftColor: '#363F89',
          paddingLeft: 12,
          marginBottom: 16
        }}
      >
        <Text
          textColor={textColor}
          fontFamily={defaultFont.fontFamily}
          fontSize={defaultFont.fontSize}
          lineHeight={defaultFont.lineHeight}
          fontStyle='italic'
        >
          {ch}
        </Text>
      </Box>
    ),
    ul: ({ children: ch }: any) => (
      <Box style={{ paddingLeft: 20, marginBottom: 16 }}>{ch}</Box>
    ),
    ol: ({ children: ch }: any) => (
      <Box style={{ paddingLeft: 20, marginBottom: 16 }}>{ch}</Box>
    ),
    li: ({ children: ch, ordered, index }: any) => {
      const bullet = ordered ? `${index + 1}. ` : '• '
      return (
        <Box style={{ flexDirection: 'row', marginBottom: 4 }}>
          <Text
            textColor={textColor}
            fontFamily={defaultFont.fontFamily}
            fontSize={defaultFont.fontSize}
            lineHeight={defaultFont.lineHeight}
            mr='04'
          >
            {bullet}
          </Text>
          <Text
            flex={1}
            textColor={textColor}
            fontFamily={defaultFont.fontFamily}
            fontSize={defaultFont.fontSize}
            lineHeight={defaultFont.lineHeight}
          >
            {ch}
          </Text>
        </Box>
      )
    }
  } as Parameters<typeof ReactMarkdown>[0]['components']

  return (
    <ReactMarkdown
      className={className}
      components={{ ...components, ...componentsOverrides }}
    >
      {children}
    </ReactMarkdown>
  )
}
